import { useMemo } from 'react'

import { useAuth as useClerkAuth } from '@clerk/clerk-react'
import { GetAppStateQuery, GetAppStateQueryVariables } from 'types/graphql'

import { QueryKey, useQuery } from 'src/utils/queryClient'

const GetAppState = gql`
  query GetAppStateQuery($organizationId: Int!, $hasOrganization: Boolean!) {
    user: me {
      ...UserDataFragmentWithCompany
      recordTypeAccesses {
        id
        recordType {
          ...RecordTypeFragment
        }
      }
      userOrganizationMemberships {
        id
        organization {
          id
        }
        canViewForeignValues
        group
        isCodeViolationsEmployeeReference
        isInspector
        isLandingPageEmployeeReference
        isOptedOutFromAicaDigest
        isPermittingEmployeeReference
        isPlanReviewer
        title
      }
    }
    organization(id: $organizationId) @include(if: $hasOrganization) {
      ...OrganizationFragmentWithUsers
      userOrganizationMemberships {
        isInspector
        user {
          ...UserDataFragment
        }
      }
      isExternalCardReaderEnabled
      aiChatIconCustomizationEnabled
      aiChatIconUrl
      aiChatLauncherBottomSpacingPx
      aiChatLauncherPosition
      aiChatLauncherSideSpacingPx
      aiChatLauncherUrlAllowList
      aiChatLauncherPopupEnabled
      aiChatLauncherPopupTitle
      aiChatLauncherPopupDescription
      aiChatScriptUrl
      aiCommunityAssistantMonthlyQuota
      allowNegativeFees
      hasRecordTypeWithApplicationAutoCheckEnabled
      hasRecordTypeWithMeetingsEnabled
      isPropertyOwnerAddressAvailable
      paymentProviderFormatted
      reportTemplateDocumentOptions {
        documentOptions {
          displayName
          documentType
          includeByDefault
        }
        reportTemplateType
      }
      finixMerchants {
        id
      }
      entityLayers {
        ...EntityLayerFragment
      }
    }
  }
`

export const useAppStateQuery = (organizationId: number | undefined) => {
  const { userId, isLoaded } = useClerkAuth()
  const hasOrganization = !!organizationId
  const queryResult = useQuery<GetAppStateQuery, GetAppStateQueryVariables>({
    queryKey: [QueryKey.AppState, userId, organizationId],
    queryDocument: GetAppState,
    variables: {
      organizationId: organizationId ?? -1,
      hasOrganization,
    },
    enabled: !!userId,
  })
  const data = useMemo(
    () => ({
      organization: queryResult.data?.organization,
      user: queryResult.data?.user,
    }),
    [queryResult.data]
  )

  const isLoading = !isLoaded || queryResult.isLoading
  return {
    ...queryResult,
    data,
    isLoading,
  }
}
